// Schreiben-Assistent Etappe 3: mehrere PDFs/Bilder zu einem PDF (Druckstapel: Anschreiben + Anlagen, „Alle drucken“).
// pdf-lib wird erst beim Drucken geladen. Bilder kommen eingepasst auf eine A4-Seite.

export interface PdfTeil { bytes: Uint8Array; mime: string; name?: string }

const A4 = { breite: 595.28, hoehe: 841.89 };

/** Bild auf A4 eingepasst (Ränder 10 mm, mittig). Reine Rechnung – testbar. */
export function bildAufA4(breite: number, hoehe: number): { x: number; y: number; breite: number; hoehe: number } {
  const rand = 28.35;
  const faktor = Math.min((A4.breite - 2 * rand) / breite, (A4.hoehe - 2 * rand) / hoehe, 1);
  const b = breite * faktor, h = hoehe * faktor;
  return { x: (A4.breite - b) / 2, y: (A4.hoehe - h) / 2, breite: b, hoehe: h };
}

/** Dateiart an den ersten Bytes – MIME-Typ und Endung sind bei Drive-Dateien nicht verlässlich. */
export function dateiArt(b: Uint8Array): 'pdf' | 'png' | 'jpg' | null {
  if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'pdf';
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'png';
  if (b[0] === 0xff && b[1] === 0xd8) return 'jpg';
  return null;
}

export async function fuegePdfZusammen(teile: PdfTeil[]): Promise<Uint8Array> {
  const { PDFDocument } = await import('pdf-lib');
  const ziel = await PDFDocument.create();
  for (const t of teile) {
    const name = t.name || 'Anlage';
    const art = dateiArt(t.bytes);
    if (!art) throw new Error(`${name}: weder PDF noch PNG/JPG – kann nicht gedruckt werden`);
    if (art === 'pdf') {
      const quelle = await PDFDocument.load(t.bytes, { ignoreEncryption: true });
      // geschützte PDFs würden leer oder kaputt übernommen – lieber sichtbar scheitern
      if (quelle.isEncrypted) throw new Error(`${name} ist geschützt (verschlüsselt) – bitte ohne Schutz speichern und erneut anhängen`);
      for (const seite of await ziel.copyPages(quelle, quelle.getPageIndices())) ziel.addPage(seite);
      continue;
    }
    const bild = art === 'png' ? await ziel.embedPng(t.bytes) : await ziel.embedJpg(t.bytes);
    const seite = ziel.addPage([A4.breite, A4.hoehe]);
    const p = bildAufA4(bild.width, bild.height);
    seite.drawImage(bild, { x: p.x, y: p.y, width: p.breite, height: p.hoehe });
  }
  return ziel.save();
}

export const base64ZuBytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export function bytesZuBase64(bytes: Uint8Array): string {
  // in Blöcken – zeichenweise wäre bei großen PDFs zu langsam
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
